import styled, { css } from 'styled-components';

export const EditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  gap: 16px;
`;

export const EditorTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.dashboardTitle};
  margin: 0 0 4px;
`;

export const EditorSubtitle = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.text.dashboardLightText};
  margin: 0 0 8px;
`;

export const PreviewArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 120px;
  min-width: 0;
  overflow: auto;
`;

export const TileRow = styled.div`
  display: flex;
  gap: 8px;
  min-height: 52px;
  border-radius: 4px;
  padding: 3px;
  transition: background 0.15s;

  ${({ $isDraggingOver, theme }) =>
    $isDraggingOver &&
    `background: ${theme.borders.dashboard}22;
     outline: 1px dashed ${theme.borders.dashboard};`}
`;

export const Tile = styled.div`
  flex: ${({ $grow }) => $grow || 1};
  min-width: 0;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: ${({ theme }) => theme.backgrounds.dashboardWidget};
  border-radius: 4px;
  border: 1.5px solid ${({ $selected, theme }) =>
    $selected ? theme.borders.workspaceSelectedItem : theme.borders.dashboard};
  cursor: pointer;
  user-select: none;
  overflow: hidden;
  transition: border-color 0.12s;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${({ $color }) => $color || '#4a90d9'};
    border-radius: 4px 0 0 4px;
  }

  ${({ $isDragging }) =>
    $isDragging &&
    css`
      opacity: 0.85;
      box-shadow: 0 6px 20px rgba(0,0,0,0.2);
    `}
`;

export const TileDragHandle = styled.div`
  cursor: grab;
  color: ${({ theme }) => theme.text.dashboardLightText};
  font-size: 13px;
  flex-shrink: 0;
  line-height: 1;

  &:active { cursor: grabbing; }
`;

export const TileName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.text.dashboardDarkText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

export const TileGrowBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.dashboardLightText};
  background: ${({ theme }) => theme.borders.dashboard}44;
  border-radius: 3px;
  padding: 1px 5px;
  flex-shrink: 0;
`;

export const SettingsPanel = styled.div`
  background: ${({ theme }) => theme.backgrounds.dashboardWidget};
  border: 1px solid ${({ theme }) => theme.borders.dashboard};
  border-radius: 6px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SettingsPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const SettingsWidgetName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.dashboardTitle};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const SettingsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const SettingsLabel = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.text.dashboardLightText};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  min-width: 44px;
`;

export const OptionButtons = styled.div`
  display: flex;
  gap: 6px;
`;

export const OptionButton = styled.button`
  height: 26px;
  padding: 0 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s;
  border: 1px solid ${({ $active, theme }) =>
    $active ? theme.borders.workspaceSelectedItem : theme.borders.dashboard};
  background: ${({ $active, theme }) =>
    $active ? theme.buttons.primary.background : 'transparent'};
  color: ${({ $active, theme }) => ($active ? theme.buttons.primary.color : theme.buttons.secondary.color)};

  &:hover {
    border-color: ${({ theme }) => theme.borders.workspaceSelectedItem};
  }
`;

export const RemoveTileButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 11px;
  color: #dc4a4a;
  padding: 2px 6px;
  border-radius: 3px;

  &:hover { background: #dc4a4a22; }
`;

export const AddTileRow = styled.div`
  display: flex;
`;

export const AddTileButton = styled.button`
  height: 28px;
  padding: 0 14px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  border: 1px dashed ${({ theme }) => theme.borders.dashboard};
  color: ${({ theme }) => theme.text.dashboardLightText};
  transition: all 0.12s;

  &:hover {
    border-color: ${({ theme }) => theme.borders.workspaceSelectedItem};
    color: ${({ theme }) => theme.borders.workspaceSelectedItem};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

export const PickerList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 4px;
`;

export const PickerItem = styled.button`
  height: 26px;
  padding: 0 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.borders.dashboard};
  color: ${({ theme }) => theme.text.dashboardDarkText};
  transition: all 0.12s;

  &:hover {
    border-color: ${({ theme }) => theme.borders.workspaceSelectedItem};
    color: ${({ theme }) => theme.borders.workspaceSelectedItem};
  }
`;

export const EditFromDashboardRow = styled.div`
  display: flex;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.borders.dashboard};
`;

export const EditFromDashboardButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.text.dashboardLightText};
  padding: 0;
  transition: color 0.12s;

  &:hover {
    color: ${({ theme }) => theme.borders.workspaceSelectedItem};
  }
`;
